import React, { useState, useEffect } from 'react';
import PropertyRequest from '../../utils/PropertyRequest';
import IHome from '../../interfaces/IHome';
import { IProperty } from '../../utils/JsonApiRequest';
import winOpen from '../../utils/openWindowWithLoadingIndicator';
import { createToast } from '../MaterialUi/Toast';
import LogError from '../../utils/LogError';
import Button, { IButtonProps } from '../MaterialUi/Button';
// import styles from "./DossierButton.scss";

export type IDossierButtonProps = {
    home: IHome | IProperty['id'];
    color?: IButtonProps['color'];
    type?: IButtonProps['type'];
    className?: string;
    useExactLocation: boolean;
} & IButtonProps;

const isHome = (home: IHome | IProperty['id']): home is IHome => {
    return home && (home as IHome).id !== undefined;
};

/* 
   window.open in iOS Safari doesn't work in async functions, so window.open must called before all requests 
   see https://stackoverflow.com/questions/13636776/javascript-cant-open-new-tab-in-safari  
*/
let win = null as Window;


/**
 * create a pricehubble dossier link of a real estate property
 */
const DossierButton: React.FC<React.PropsWithChildren<IDossierButtonProps>> = ({ home = null, children = null, color = null, type = 'button', className = null, useExactLocation, ...rest}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [currHome, setCurrHome] = useState<IHome>(isHome(home) ? home : ({} as IHome));

    useEffect(() => {
        if (isHome(home)) {
            setCurrHome(home);
        } else {
            setCurrHome({} as IHome);
        }
    }, [home]);

    const createDossier = () => {
        if (isHome(home)) {
            win = winOpen();
            createDossierLink(currHome);
        } else {
            if (typeof home === 'string') {
                win = winOpen();
                setIsLoading(true);
                PropertyRequest.getProperty(home)
                    .then((property) => {
                        const newHome = PropertyRequest.data2Home(property);
                        setCurrHome(newHome);
                        createDossierLink(newHome);
                        // setIsLoading(false);
                    })
                    .catch((e) => {
                        win?.close();
                        console.error(e);
                        setIsLoading(false);
                    });
            }
        }
    };

    const createDossierLink = (home) => {
        setIsLoading(true);
        PropertyRequest.getDossierLink(home.uuid, useExactLocation)
            .then((url) => {
                win?.location?.assign(url);
                win?.focus();
                setIsLoading(false);
            })
            .catch((e) => {
                win?.close();
                setIsLoading(false);
                console.error(e);
                if (typeof e === 'string') {
                    createToast(e, 'error');
                } else {
                    createToast('Fehler beim Erstellen des Dossiers.', 'error');
                }
                LogError.add(e, {
                    title: 'Fehler beim Erstellen des Dossier Links',
                    error_code: 'DossierButton.tsx:createDossierLink',
                    request_data: { home }
                });
            });
    };

    const dossierUrl = !useExactLocation &&
        currHome.dossier_url && currHome.dossier_url_valid_until && currHome.dossier_url_valid_until > new Date(Date.now() + 24 * 60 * 60 * 1000)
        ? currHome.dossier_url
        : null;

    return (
        <Button
            {...rest}
            className={className}
            color={color}
            type={type}
            target="_blank"
            href={dossierUrl}
            onClick={dossierUrl ? null : createDossier}
            loading={isLoading}
        >
            {children}
        </Button>
    );
};

export default DossierButton;
